import { ExternalLink } from "lucide-react";

/**
 * TrustedReferences — a compact block of outbound links to authoritative
 * industry bodies. Serves two goals:
 *   1. UX — gives visitors credible sources they can verify against.
 *   2. SEO — establishing topical relevance by citing high-authority domains
 *      is a well-documented trust signal (helps E-E-A-T ratings).
 *
 * Pass `refs = [{ label, href, agency }]`. Anchors use rel="noopener" so
 * link equity still flows outward (no nofollow), which is standard SEO
 * practice for editorial references to authoritative sources.
 */
export default function TrustedReferences({ eyebrow = "AUTHORITATIVE REFERENCES", title, refs = [], className = "" }) {
  if (!refs.length) return null;
  return (
    <section className={`section bg-[var(--cream)] ${className}`} aria-labelledby="trusted-refs-heading">
      <div className="max-w-[1200px] mx-auto">
        <span className="eyebrow-dark">{eyebrow}</span>
        {title && (
          <h2 id="trusted-refs-heading" className="font-display mt-5 text-[clamp(28px,3vw,40px)] leading-[1.1] text-[var(--navy)] max-w-2xl">
            {title}
          </h2>
        )}
        <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10" data-testid="trusted-refs">
          {refs.map((r) => (
            <li key={r.href}>
              <a
                href={r.href}
                target="_blank"
                rel="noopener"
                className="group block border border-[var(--line)] bg-white p-4 hover:border-[var(--navy)] transition-colors"
                data-testid={`ref-${r.agency.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="text-[10px] tracking-widest text-[var(--gold)] font-bold flex items-center gap-1.5">
                  <ExternalLink size={10} /> {r.agency}
                </div>
                <div className="font-display text-base text-[var(--navy)] mt-2 leading-snug group-hover:underline decoration-[var(--gold)] decoration-2 underline-offset-4">
                  {r.label}
                </div>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
